import React, { useEffect, useRef, useState } from "react";
import { journals } from "../lib/api";
import { toast } from "sonner";
import { BookHeart, X, Check } from "lucide-react";

const MAX_CHARS = 10000;

/**
 * SaveToJournalModal — subtle review-before-save flow.
 * User can edit the pre-filled title/content before committing. No silent
 * auto-saves. Triggers a paywall (via `onLockedWrite`) if backend returns 402.
 */
export default function SaveToJournalModal({
  open,
  initialTitle = "",
  initialContent = "",
  source = "manual",
  sourceRef = null,
  onClose,
  onSaved,
  onLockedWrite,
}) {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setContent(initialContent);
      setBusy(false);
      // Focus the textarea shortly after paint
      setTimeout(() => areaRef.current?.focus(), 60);
    }
  }, [open, initialTitle, initialContent]);

  if (!open) return null;

  const remaining = MAX_CHARS - content.length;
  const overflow = remaining < 0;

  const save = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error("Add something to save.");
      return;
    }
    if (overflow) {
      toast.error(`Please shorten to ${MAX_CHARS.toLocaleString()} characters.`);
      return;
    }
    setBusy(true);
    try {
      const created = await journals.create({
        title: title.trim() || null,
        content: trimmed,
        source,
        source_ref: sourceRef || null,
      });
      toast.success("Saved to your journal");
      onSaved?.(created);
      onClose?.();
    } catch (err) {
      if (err?.response?.status === 402) {
        onLockedWrite?.();
        onClose?.();
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't save to journal");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="save-to-journal-modal"
      className="fixed inset-0 z-[100] bg-[#2E2E2E]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 fade-in-up"
      onClick={onClose}
    >
      <div
        className="bg-[#F9FAF7] rounded-t-3xl sm:rounded-3xl w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-[0_-8px_40px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#F9FAF7]/90 backdrop-blur-sm flex items-center justify-between p-5 border-b border-[#DCE5DB]">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <BookHeart size={15} />
            </span>
            <div>
              <h2 className="font-display text-lg text-[#2E2E2E]">Save to my Journal</h2>
              <p className="text-xs text-[#6B6B6B] mt-0.5">Review and edit before saving.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="save-to-journal-close"
            className="text-[#6B6B6B] hover:text-[#2E2E2E] p-2 rounded-full hover:bg-[#EEF3EC] transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          <input
            data-testid="save-to-journal-title"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 120))}
            placeholder="Title (optional)"
            className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition mb-3"
          />
          <textarea
            ref={areaRef}
            data-testid="save-to-journal-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={10}
            className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px] leading-[1.75] text-[#2E2E2E] resize-none"
          />
          <div className="flex items-center justify-between mt-3 gap-2">
            <span
              className={`text-xs ${overflow ? "text-[#A86B4A] font-medium" : "text-[#6B6B6B]"}`}
            >
              {content.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="text-sm text-[#6B6B6B] hover:text-[#2E2E2E] rounded-full px-4 py-2 hover:bg-[#EEF3EC] transition"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy || overflow || !content.trim()}
                data-testid="save-to-journal-submit"
                className="bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-50 text-white rounded-full px-5 py-2 text-sm font-medium transition inline-flex items-center gap-1.5 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
              >
                <Check size={14} /> {busy ? "Saving..." : "Save to Journal"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
